import { useState } from 'react';
import { Banknote, Plus, Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { LoadingBlock } from '@/components/ui/Loader';
import { QueryError } from '@/components/ui/QueryError';
import { SALARY_PAYMENT_MODE_LABEL } from '@/features/institute/staff/constants';
import { useApiQuery } from '@/hooks/useApiQuery';
import { toast } from '@/hooks/useToast';
import { ApiRequestError } from '@/lib/apiClient';
import { formatCurrency, formatDate, formatMonth } from '@/lib/format';
import type { SalaryPayment, SalaryPaymentMonth } from '@/types/payroll';
import type { SalaryPaymentMode } from '@/types/staff';
import { payrollApi } from '../api/payroll.api';
import { RecordSalaryPaymentModal } from './RecordSalaryPaymentModal';

export interface SalaryPaymentsCardProps {
  staffUuid: string;
  staffName: string;
  /** How they are enrolled as being paid — what a new record starts on. */
  defaultMode: SalaryPaymentMode | null;
  canManage: boolean;
}

/**
 * The last five months of salary, as actually paid.
 *
 * Each month beside what was due under the salary in force then, so a month
 * paid short — or not at all — reads as such. Recording a month and removing a
 * wrong record are `PAYROLL_MANAGE`; everyone who can see the salary sees this.
 */
export function SalaryPaymentsCard({ staffUuid, staffName, defaultMode, canManage }: SalaryPaymentsCardProps) {
  const { data, isLoading, error, refetch } = useApiQuery(
    () => payrollApi.listSalaryPayments(staffUuid),
    [staffUuid],
  );
  const [recording, setRecording] = useState<SalaryPaymentMonth | null>(null);
  const [removing, setRemoving] = useState<SalaryPayment | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);

  async function remove() {
    if (!removing) return;
    setIsRemoving(true);
    try {
      await payrollApi.removeSalaryPayment(removing.uuid);
      toast.success('Payment record removed', `${formatMonth(`${removing.month}-01`)} can be recorded again.`);
      setRemoving(null);
      refetch();
    } catch (caught) {
      toast.error(
        'Could not remove that record',
        caught instanceof ApiRequestError ? caught.message : 'Something went wrong. Please try again.',
      );
    } finally {
      setIsRemoving(false);
    }
  }

  const months = data?.months ?? [];

  return (
    <div className="bg-white dark:bg-slate-900 border border-slate-200/90 dark:border-slate-800 rounded-2xl p-4 space-y-3">
      <h3 className="text-base font-extrabold text-slate-900 dark:text-white flex items-center gap-2 border-b border-slate-100 dark:border-slate-800 pb-2.5">
        <Banknote className="h-4 w-4 text-emerald-600" aria-hidden />
        <span>Salary payments — last 5 months</span>
      </h3>

      {isLoading && <LoadingBlock label="Loading salary payments" />}
      {error && <QueryError title="Could not load the salary payments" error={error} onRetry={refetch} />}

      {!isLoading && !error && months.length === 0 && (
        <p className="text-sm text-slate-500 dark:text-slate-400">
          They joined after these months, so there is no salary to show yet.
        </p>
      )}

      {!isLoading && !error && months.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <caption className="sr-only">{staffName}'s salary for the last five months, newest first</caption>
            <thead>
              <tr className="border-b border-slate-200 dark:border-slate-800 text-xs font-bold text-slate-500 dark:text-slate-400">
                <th scope="col" className="py-2 px-3">Month</th>
                <th scope="col" className="py-2 px-3 text-right">Due</th>
                <th scope="col" className="py-2 px-3 text-right">Paid</th>
                <th scope="col" className="py-2 px-3">Paid on</th>
                <th scope="col" className="py-2 px-3">Mode</th>
                {canManage && <th scope="col" className="py-2 px-3 text-right"><span className="sr-only">Actions</span></th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {months.map((row) => {
                const payment = row.payment;
                const isShort = payment && row.due && Number(payment.amount) < Number(row.due);
                return (
                  <tr key={row.month}>
                    <td className="py-2.5 px-3 whitespace-nowrap font-semibold text-slate-900 dark:text-white">
                      {formatMonth(`${row.month}-01`)}
                    </td>
                    <td className="py-2.5 px-3 text-right tabular-nums text-slate-600 dark:text-slate-300">
                      {row.due ? formatCurrency(row.due) : '—'}
                    </td>
                    <td className="py-2.5 px-3 text-right tabular-nums">
                      {payment ? (
                        <span className={isShort ? 'font-bold text-amber-600 dark:text-amber-400' : 'font-bold text-emerald-600 dark:text-emerald-400'}>
                          {formatCurrency(payment.amount)}
                        </span>
                      ) : (
                        <Badge tone="warning">Not paid</Badge>
                      )}
                    </td>
                    <td className="py-2.5 px-3 whitespace-nowrap text-slate-700 dark:text-slate-300">
                      {payment ? formatDate(payment.paidOn) : '—'}
                    </td>
                    <td className="py-2.5 px-3 text-slate-700 dark:text-slate-300">
                      {payment ? (
                        <>
                          {SALARY_PAYMENT_MODE_LABEL[payment.mode]}
                          {payment.reference && (
                            <span className="block font-mono text-xs text-slate-400">{payment.reference}</span>
                          )}
                        </>
                      ) : (
                        '—'
                      )}
                    </td>
                    {canManage && (
                      <td className="py-2.5 px-3 text-right">
                        {payment ? (
                          <button
                            type="button"
                            onClick={() => setRemoving(payment)}
                            aria-label={`Remove the ${formatMonth(`${row.month}-01`)} payment record`}
                            title="Remove this record"
                            className="inline-grid h-8 w-8 place-items-center rounded-lg text-slate-400 hover:bg-rose-50 hover:text-rose-600 dark:hover:bg-rose-950/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-500"
                          >
                            <Trash2 className="h-4 w-4" aria-hidden />
                          </button>
                        ) : (
                          <Button size="sm" variant="secondary" leftIcon={<Plus className="h-3.5 w-3.5" />} onClick={() => setRecording(row)}>
                            Record
                          </Button>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {recording && (
        <RecordSalaryPaymentModal
          staffUuid={staffUuid}
          staffName={staffName}
          month={recording}
          defaultMode={defaultMode}
          onClose={() => setRecording(null)}
          onSaved={() => {
            toast.success('Payment recorded', `${formatMonth(`${recording.month}-01`)} is marked as paid.`);
            setRecording(null);
            refetch();
          }}
        />
      )}

      <ConfirmDialog
        isOpen={Boolean(removing)}
        tone="danger"
        title={`Remove the ${removing ? formatMonth(`${removing.month}-01`) : ''} payment record?`}
        description="Use this for a payment entered by mistake. The month shows as not paid until it is recorded again; the removed record stays in the audit history."
        confirmLabel="Remove record"
        isLoading={isRemoving}
        onConfirm={() => void remove()}
        onCancel={() => setRemoving(null)}
      />
    </div>
  );
}
